import { Children, createContext, useContext, useState } from 'react';
import type { IAccordionContext, IAccordionProps } from './interfaces';
import { Column } from '../Column';
import { Divider } from '../Divider';
import { Colors } from '~/themes/colors';

const AccordionContext = createContext<IAccordionContext>({
  isItemOpen: () => false,
  onToggle: () => {},
});

export const useAccordionContext = () => useContext(AccordionContext);

export const Accordion = ({
  defaultOpenKey,
  allowMultiple = false,
  SeparatorComponent = () => <Divider color={Colors.neutral_150} height={1} marginVertical={12} />,
  children,
}: IAccordionProps) => {
  const [openKeys, setOpenKeys] = useState<Set<string>>(
    defaultOpenKey ? new Set([defaultOpenKey]) : new Set(),
  );

  const isItemOpen = (key: string) => openKeys.has(key);

  const onToggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (!allowMultiple) {
          next.clear();
        }
        next.add(key);
      }
      return next;
    });
  };

  const childArray = Children.toArray(children);
  const content = childArray.flatMap((child, index) =>
    index < childArray.length - 1 ? [child, <SeparatorComponent key={`sep-${index}`} />] : [child],
  );

  return (
    <AccordionContext.Provider value={{ isItemOpen, onToggle }}>
      <Column>{content}</Column>
    </AccordionContext.Provider>
  );
};
